import React, { useEffect, useState } from "react";
import {
    ArrowDownTrayIcon,
    ListBulletIcon,
    PencilIcon,
} from "@heroicons/react/24/outline";
import { GithubPicker } from "react-color";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import htmlDocx from "html-docx-js/dist/html-docx";
import { Menu } from "@headlessui/react";

// HEX to RGB helper
function hexToRgb(hex) {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((x) => x + x)
            .join("");
    }
    const num = parseInt(hex, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
}

const customColors = [
    "#ddad9e", "#f8e0c5", "#fcd39e", "#eab976", "#dec487", "#e6dcab", "#f1efb9", "#e2f1b5",
    "#bae295", "#aeea84", "#bbe4ae", "#aceea4", "#8cdd85", "#85eca4", "#bdf3d7", "#87fdd1",
    "#6fdcb4", "#a2dcf1", "#8cd2ef", "#aad2ec", "#bedcf0", "#72b0db", "#9bbbfc", "#a4a3dd",
    "#a8a1e9", "#c7bdf2", "#b29fdf", "#d789fe", "#e5a3f6", "#f4bff8", "#e895b1", "#f597a4",
    "#ffffff", "#000000"
];

const EditableHeader = ({ initialText, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(initialText);
    const [prevText, setPrevText] = useState(initialText);

    const handleEdit = () => {
        setPrevText(text);
        setIsEditing(true);
    };

    const handleSave = () => {
        onSave(text);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setText(prevText);
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    return (
        <div className="flex items-center gap-2">
            {isEditing ? (
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="border rounded px-2 py-1 text-sm w-full"
                    autoFocus
                />
            ) : (
                <>
                    <span>{text}</span>
                    <button
                        onClick={handleEdit}
                        className="p-1 hover:bg-gray-100 rounded transition-colors edit-button"
                    >
                        <PencilIcon className="w-4 h-4 text-gray-500" />
                    </button>
                </>
            )}
        </div>
    );
};

const ExportWrapper = ({
    children,
    title,
    visibleItems,
    visibleItemsState,
    setVisibleItemsState,
    headerBgColor = "#ffffff",
    headerTextColor = "#000000",
}) => {
    const wrapperRef = React.useRef(null);
    const { t } = useTranslation();

    // PNG Export
    const downloadAsImage = async () => {
        try {
            const element = wrapperRef.current;
            const editButtons = element.querySelectorAll(".edit-button");
            editButtons.forEach((button) => {
                button.style.display = "none";
            });

            const style = document.createElement("style");
            style.innerHTML = `
                td[rowspan], td, th {
                    border: 1px solid #e5e7eb !important;
                }
            `;
            document.head.appendChild(style);

            const canvas = await html2canvas(element);

            document.head.removeChild(style);
            editButtons.forEach((button) => {
                button.style.display = "";
            });

            const image = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = image;
            link.download = `${title}.png`;
            link.click();
        } catch (error) {
            console.error("Error generating image:", error);
        }
    };

    // PDF Export with header color support
    const exportAsPDF = async () => {
        const element = wrapperRef.current;
        const editButtons = element.querySelectorAll(".edit-button");
        editButtons.forEach((button) => {
            button.style.display = "none";
        });

        const table = element.querySelector("table");
        const bgRgb = hexToRgb(headerBgColor);
        const textRgb = hexToRgb(headerTextColor);

        const doc = new jsPDF({
            orientation: "landscape",
            unit: "pt",
            format: "a4",
        });

        autoTable(doc, {
            html: table,
            startY: 30,
            styles: {
                overflow: "linebreak",
                fontSize: 10,
                cellPadding: 4,
                valign: "middle",
            },
            headStyles: {
                fillColor: bgRgb,
                textColor: textRgb,
                lineWidth: 0.5,        // Adds visible borders
                lineColor: [180, 180, 180],
            },
            theme: "grid",
            margin: { left: 20, right: 20 },
            tableWidth: "auto",
            didDrawPage: function (data) {
                doc.setFontSize(16);
                doc.text(
                    title || "Table Export",
                    doc.internal.pageSize.getWidth() / 2,
                    22,
                    { align: "center" }
                );
            },
        });

        editButtons.forEach((button) => {
            button.style.display = "";
        });

        doc.save(`${title}.pdf`);
    };

    // Word Export
    const exportAsWord = async () => {
        const element = wrapperRef.current;
        const editButtons = element.querySelectorAll(".edit-button");
        editButtons.forEach((button) => {
            button.style.display = "none";
        });

        // Prepare style for Word export
        const style = `
      <style>
        table, th, td {
          border: 1px solid #e5e7eb !important;
          border-collapse: collapse;
        }
        th, td {
          padding: 8px;
        }
      </style>
    `;
        const html = style + element.innerHTML;
        const converted = htmlDocx.asBlob(html);

        editButtons.forEach((button) => {
            button.style.display = "";
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(converted);
        link.download = `${title}.docx`;
        link.click();
    };

    return (
        <div>
            <div className="flex flex-col lg:flex-row  justify-between items-center">
                <h2 className="text-xl font-semibold whitespace-nowrap ">
                    {t("CategoryResults.TableHeader")}
                </h2>
                <div className="flex flex-collg:flex-row w-full justify-between lg:justify-end gap-2 py-2 items-center">
                    <button
                        onClick={() => setVisibleItemsState(!visibleItemsState)}
                        className="flex w-full lg:w-fit items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm"
                    >
                        <ListBulletIcon className="w-4 h-4" />
                        {t("CategoryResults.options")}
                    </button>
                    {/* Export Dropdown */}
                    <Menu as="div" className="relative">
                        <Menu.Button className="flex w-full lg:w-fit items-center gap-2 px-3 py-1 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm">
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            {t("CategoryResults.Export")}
                        </Menu.Button>
                        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded shadow-lg focus:outline-none z-50">
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={downloadAsImage}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        PNG
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={exportAsPDF}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        PDF
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={exportAsWord}
                                        className={`block w-full text-left px-4 py-2 text-sm ${active ? "bg-gray-100" : ""
                                            }`}
                                    >
                                        Word
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Menu>
                </div>
            </div>
            {visibleItems && visibleItems}
            <div className="overflow-x-auto border ">
                <div ref={wrapperRef} className="bg-white min-w-[1024px]">
                    {children}
                </div>
            </div>
        </div>
    );
};

const ColumnToggle = ({
    label,
    checked,
    onChange,
    disabled = false,
}) => (
    <label className="flex items-center space-x-2 text-sm">
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className="rounded border-gray-300 text-black focus:ring-black"
        />
        <span className={disabled ? "text-gray-400" : "text-gray-700"}>{label}</span>
    </label>
);

const formatExcerpt = (text) => `"${text}"`;

const ExcerptsList = ({ excerpts }) => {
    if (!excerpts || !excerpts.length)
        return (
            <span className="text-gray-500 text-sm">No excerpts</span>
        );

    return (
        <div className="space-y-2 text-sm">
            {excerpts.map((excerpt) => (
                <div key={excerpt.uuid}>{formatExcerpt(excerpt.text)}</div>
            ))}
        </div>
    );
};

const countUniqueFiles = (excerpts) => {
    if (!excerpts || !excerpts.length) return 0;
    const uniqueFiles = new Set();
    excerpts.forEach((excerpt) => {
        if (excerpt && excerpt.analysis_parent_id) {
            uniqueFiles.add(excerpt.analysis_parent_id);
        }
    });
    return uniqueFiles.size;
};

const ColorPickerSection = ({ label, color, onChange }) => {
    const [showPicker, setShowPicker] = useState(false);

    return (
        <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            <div className="relative">
                <button
                    className="w-full h-8 rounded border flex items-center px-2 hover:bg-gray-50"
                    onClick={() => setShowPicker(!showPicker)}
                >
                    <div
                        className="w-6 h-6 rounded mr-2 border"
                        style={{ backgroundColor: color }}
                    />
                    <span className="text-sm">{color}</span>
                </button>
                {showPicker && (
                    <div className="absolute z-10 mt-1">
                        <div
                            className="fixed inset-0"
                            onClick={() => setShowPicker(false)}
                        />
                        <GithubPicker
                            color={color}
                            colors={customColors}
                            onChange={(color) => {
                                onChange(color.hex);
                                setShowPicker(false);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const CategoryTable = ({ data }) => {
    const [headerBgColor, setHeaderBgColor] = useState("#ffffff");
    const [headerTextColor, setHeaderTextColor] = useState("#000000");
    const { t } = useTranslation();

    const [columns, setColumns] = useState({
        category: true,
        categoryDescription: true,
        categoryExcerpts: true,
        categoryExcerptCount: true,
        categoryFileCount: true,
        subcategories: false,
        subcategoryDescription: false,
        subcategoryExcerpts: false,
        subcategoryExcerptCount: false,
        subcategoryFileCount: false,
    });

    const [headerTexts, setHeaderTexts] = useState({
        category: t("CategoryResults.categoryName"),
        categoryExcerptCount: t("CategoryResults.categoryExcerptCount"),
        categoryFileCount: t("CategoryResults.categoryFileCount"),
        categoryExcerpts: t("CategoryResults.categoryExcerpts"),
        subcategories: t("CategoryResults.subcategories"),
        subcategoryExcerptCount: t("CategoryResults.subcategoryExcerptCount"),
        subcategoryFileCount: t("CategoryResults.subcategoryFileCount"),
        subcategoryExcerpts: t("CategoryResults.subcategoryExcerpts"),
    });

    const handleHeaderUpdate = (key, newText) => {
        setHeaderTexts((prev) => ({
            ...prev,
            [key]: newText,
        }));
    };

    const rootCategories = data.filter((item) => !item.parent);
    const subCategories = data.filter((item) => item.parent);

    const getSubcategoriesForParent = (parentUuid) => {
        return subCategories.filter((sub) => sub.parent === parentUuid);
    };

    const getLastThreeExcerpts = (excerpts) => {
        if (!excerpts || !excerpts.length) return [];
        return excerpts.slice(-3);
    };

    const toggleColumn = (columnName) => {
        setColumns((prev) => {
            const newColumns = { ...prev, [columnName]: !prev[columnName] };

            if (columnName === "category" && !newColumns.category) {
                newColumns.categoryDescription = false;
                newColumns.categoryExcerpts = false;
                newColumns.categoryStats = false;
                newColumns.categoryExcerptCount = false;
                newColumns.categoryFileCount = false;
            }
            if (columnName === "subcategories" && !newColumns.subcategories) {
                newColumns.subcategoryDescription = false;
                newColumns.subcategoryExcerpts = false;
                newColumns.subcategoryStats = false;
                newColumns.subcategoryExcerptCount = false;
                newColumns.subcategoryFileCount = false;
            }
            return newColumns;
        });
    };
    const [visibleItems, setVisibleItems] = useState(null);
    const [visibleItemsState, setVisibleItemsState] = useState(false);

    useEffect(() => {
        if (visibleItemsState === true) {
            setVisibleItems(
                <div className="grid lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg my-2">
                    <div className="space-y-2">
                        <h3 className="font-medium mb-2">
                            {t("CategoryResults.categoryColumns")}
                        </h3>
                        <ColumnToggle
                            label={t("CategoryResults.categoryName")}
                            checked={columns.category}
                            onChange={() => toggleColumn("category")}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.categoryDescription")}
                            checked={columns.categoryDescription}
                            onChange={() => toggleColumn("categoryDescription")}
                            disabled={!columns.category}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.categoryExcerpts")}
                            checked={columns.categoryExcerpts}
                            onChange={() => toggleColumn("categoryExcerpts")}
                            disabled={!columns.category}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.categoryExcerptCount")}
                            checked={columns.categoryExcerptCount}
                            onChange={() => toggleColumn("categoryExcerptCount")}
                            disabled={!columns.category}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.categoryFileCount")}
                            checked={columns.categoryFileCount}
                            onChange={() => toggleColumn("categoryFileCount")}
                            disabled={!columns.category}
                        />
                    </div>
                    <div className="space-y-2">
                        <h3 className="font-medium mb-2">
                            {t("CategoryResults.subcategoryColumns")}
                        </h3>
                        <ColumnToggle
                            label={t("CategoryResults.subcategories")}
                            checked={columns.subcategories}
                            onChange={() => toggleColumn("subcategories")}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.subcategoryDescription")}
                            checked={columns.subcategoryDescription}
                            onChange={() => toggleColumn("subcategoryDescription")}
                            disabled={!columns.subcategories}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.subcategoryExcerpts")}
                            checked={columns.subcategoryExcerpts}
                            onChange={() => toggleColumn("subcategoryExcerpts")}
                            disabled={!columns.subcategories}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.subcategoryExcerptCount")}
                            checked={columns.subcategoryExcerptCount}
                            onChange={() => toggleColumn("subcategoryExcerptCount")}
                            disabled={!columns.subcategories}
                        />
                        <ColumnToggle
                            label={t("CategoryResults.subcategoryFileCount")}
                            checked={columns.subcategoryFileCount}
                            onChange={() => toggleColumn("subcategoryFileCount")}
                            disabled={!columns.subcategories}
                        />
                    </div>
                    <div className="flex flex-col items-start justify-start">
                        <h3 className="font-medium mb-4">
                            {t("CategoryResults.tableHeaderStyling")}
                        </h3>
                        <div className="grid gap-4">
                            <ColorPickerSection
                                label={t("CategoryResults.headerBackgroundColor")}
                                color={headerBgColor}
                                onChange={setHeaderBgColor}
                            />
                            <ColorPickerSection
                                label={t("CategoryResults.headerTextColor")}
                                color={headerTextColor}
                                onChange={setHeaderTextColor}
                            />
                        </div>
                    </div>
                </div>
            );
        } else {
            setVisibleItems(null);
        }
    }, [visibleItemsState, columns, headerBgColor, headerTextColor, t]);

    const headerStyle = {
        backgroundColor: headerBgColor,
        color: headerTextColor,
        verticalAlign: "middle",
    };

    return (
        <div className="space-y-4 w-full">
            <div>
                <div className="border rounded-lg border-black shadow min-w-full">
                    <div className="border-b p-4 flex flex-col w-full  justify-between items-center">
                        <div className="w-full">
                            <ExportWrapper
                                title={t("CategoryResults.TableHeader")}
                                visibleItems={visibleItems}
                                visibleItemsState={visibleItemsState}
                                setVisibleItemsState={setVisibleItemsState}
                                headerBgColor={headerBgColor}
                                headerTextColor={headerTextColor}
                            >
                                <table className="w-full border-collapse">
                                    <thead style={headerStyle}>
                                        <tr className="h-full">
                                            {columns.category && (
                                                <th
                                                    className="align-middle p-3 text-left border"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.category}
                                                        onSave={(text) => handleHeaderUpdate("category", text)}
                                                    />
                                                </th>
                                            )}
                                            {columns.categoryExcerpts && (
                                                <th
                                                    className="align-middle p-3 text-left border"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.categoryExcerpts}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("categoryExcerpts", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.categoryExcerptCount && (
                                                <th
                                                    className="align-middle p-3 text-left border text-sm"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.categoryExcerptCount}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("categoryExcerptCount", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.categoryFileCount && (
                                                <th
                                                    className="align-middle p-3 text-left border text-sm"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.categoryFileCount}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("categoryFileCount", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.subcategories && (
                                                <th
                                                    className="align-middle p-3 text-left border"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.subcategories}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("subcategories", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.subcategoryExcerpts && (
                                                <th
                                                    className="align-middle p-3 text-left border"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.subcategoryExcerpts}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("subcategoryExcerpts", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.subcategoryExcerptCount && (
                                                <th
                                                    className="align-middle p-3 text-left border text-sm"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.subcategoryExcerptCount}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("subcategoryExcerptCount", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                            {columns.subcategoryFileCount && (
                                                <th
                                                    className="align-middle p-3 text-left border text-sm"
                                                    style={headerStyle}
                                                >
                                                    <EditableHeader
                                                        initialText={headerTexts.subcategoryFileCount}
                                                        onSave={(text) =>
                                                            handleHeaderUpdate("subcategoryFileCount", text)
                                                        }
                                                    />
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rootCategories.map((category) => {
                                            const subs = getSubcategoriesForParent(category.uuid);
                                            const hasSubcategories = subs.length > 0;
                                            const categoryExcerpts = getLastThreeExcerpts(
                                                category.excerpts
                                            );
                                            const rowCount =
                                                columns.subcategories && hasSubcategories
                                                    ? subs.length
                                                    : 1;

                                            return Array.from({ length: rowCount }).map((_, index) => (
                                                <tr key={`${category.uuid}-${index}`}>
                                                    {/* Category column */}
                                                    {columns.category && index === 0 && (
                                                        <td className="p-3 border" rowSpan={rowCount}>
                                                            <div className="font-medium">{category.name}</div>
                                                            {columns.categoryDescription && (
                                                                <div className="text-sm text-gray-600">
                                                                    {category.description}
                                                                </div>
                                                            )}
                                                        </td>
                                                    )}

                                                    {/* Category Excerpts column */}
                                                    {columns.categoryExcerpts && index === 0 && (
                                                        <td className="p-3 border" rowSpan={rowCount}>
                                                            <ExcerptsList excerpts={categoryExcerpts} />
                                                        </td>
                                                    )}
                                                    {/* Category Excerpt Count column */}
                                                    {columns.categoryExcerptCount && index === 0 && (
                                                        <td className="p-3 border text-center" rowSpan={rowCount}>
                                                            <div className="text-sm">
                                                                {category.excerpts?.length || 0}
                                                            </div>
                                                        </td>
                                                    )}

                                                    {/* Category File Count column */}
                                                    {columns.categoryFileCount && index === 0 && (
                                                        <td className="p-3 border text-center" rowSpan={rowCount}>
                                                            <div className="text-sm">
                                                                {countUniqueFiles(category.excerpts)}
                                                            </div>
                                                        </td>
                                                    )}

                                                    {/* Subcategory columns */}
                                                    {columns.subcategories &&
                                                        (hasSubcategories ? (
                                                            <>
                                                                <td className="p-3 border">
                                                                    <div className="font-medium">
                                                                        {subs[index].name}
                                                                    </div>
                                                                    {columns.subcategoryDescription && (
                                                                        <div className="text-sm text-gray-600">
                                                                            {subs[index].description}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                {columns.subcategoryExcerpts && (
                                                                    <td className="p-3 border">
                                                                        <ExcerptsList
                                                                            excerpts={getLastThreeExcerpts(
                                                                                subs[index].excerpts
                                                                            )}
                                                                        />
                                                                    </td>
                                                                )}
                                                                {columns.subcategoryExcerptCount && (
                                                                    <td className="p-3 border text-center">
                                                                        <div className="text-sm">
                                                                            {subs[index].excerpts?.length || 0}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                {columns.subcategoryFileCount && (
                                                                    <td className="p-3 border text-center">
                                                                        <div className="text-sm">
                                                                            {countUniqueFiles(subs[index].excerpts)}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                            </>
                                                        ) : (
                                                            index === 0 && (
                                                                <>
                                                                    <td className="p-3 border">
                                                                        <span className="text-gray-500 text-sm">
                                                                            {t("CategoryResults.NoCategories")}
                                                                        </span>
                                                                    </td>
                                                                    {columns.subcategoryExcerpts && (
                                                                        <td className="p-3 border">
                                                                            <span className="text-gray-500 text-sm">
                                                                                {t("CategoryResults.NoSubcategories")}
                                                                            </span>
                                                                        </td>
                                                                    )}
                                                                    {columns.subcategoryExcerptCount && (
                                                                        <td className="p-3 border text-center">
                                                                            <span className="text-gray-500 text-sm">0</span>
                                                                        </td>
                                                                    )}
                                                                    {columns.subcategoryFileCount && (
                                                                        <td className="p-3 border text-center">
                                                                            <span className="text-gray-500 text-sm">0</span>
                                                                        </td>
                                                                    )}
                                                                </>
                                                            )
                                                        ))}
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </table>
                            </ExportWrapper>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HierarchicalTables = ({ data }) => {
    return (
        <div className="space-y-8  ">
            <CategoryTable data={data} />
        </div>
    );
};

export default HierarchicalTables;
